import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Container, MenuItem, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { localGet } from '../../../utils/localStorageUtils';
import { getIntegration, saveIntegration, syncNow, uploadRosterZip } from '../../../utils/integrationsService';

/**
 * SIS connection (DiscloSure admins only). Three ways to bring in a district's roster:
 *  - OneRoster (Nov 2026, free standard): the district's roster server address + client ID/secret;
 *    works with PowerSchool, Infinite Campus, Skyward, ClassLink and Ed-Fi.
 *  - File: upload the district's OneRoster CSV export (.zip).
 *  - Clever (paid by DiscloSure per district).
 * Each sync saves student demographics and class schedules (server only) and compares rosters.
 * It does not create or remove accounts yet.
 */
const Label = ({ htmlFor, children }) => (
  // Labels sit above the boxes: the portal's theme makes MUI's floating labels overlap values.
  <Typography component="label" htmlFor={htmlFor} sx={{ fontSize: '14px', fontWeight: 600, display: 'block', mb: '4px' }}>
    {children}
  </Typography>
);

const SisPanel = ({ data }) => {
  const theme = useTheme();
  const district = data?.id;
  const fileRef = useRef(null);
  const [user, setUser] = useState(null);
  const [state, setState] = useState(null);
  const [mode, setMode] = useState('oneroster'); // oneroster | file | clever
  const [form, setForm] = useState({ cleverDistrictId: '', oneRosterBaseUrl: '', oneRosterAuth: 'oauth2',
                                     oneRosterTokenUrl: '', clientId: '', clientSecret: '', enabled: false });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => setUser(await localGet('user', true)))(); }, []);
  const isAdmin = user?.permission === 'disclosureAdmin';

  const load = async () => {
    const res = await getIntegration(district);
    if (!res.ok) { setMsg(res.message); return; }
    setState(res);
    setMode((m) => (m === 'file' ? m : res.source || 'oneroster'));
    setForm((f) => ({ ...f, cleverDistrictId: res.cleverDistrictId || '', oneRosterBaseUrl: res.oneRosterBaseUrl || '',
                      oneRosterAuth: res.oneRosterAuth || 'oauth2', oneRosterTokenUrl: res.oneRosterTokenUrl || '',
                      clientId: '', clientSecret: '', enabled: !!res.enabled }));
  };
  useEffect(() => { if (isAdmin && district) load(); /* eslint-disable-next-line */ }, [isAdmin, district]);
  if (!isAdmin) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: k === 'enabled' ? e.target.checked : e.target.value });
  const save = async () => {
    setBusy(true); setMsg('');
    const settings = mode === 'clever'
      ? { source: 'clever', cleverDistrictId: form.cleverDistrictId.trim(), enabled: form.enabled }
      : { source: 'oneroster', oneRosterBaseUrl: form.oneRosterBaseUrl.trim(), oneRosterAuth: form.oneRosterAuth,
          oneRosterTokenUrl: form.oneRosterTokenUrl.trim(), enabled: form.enabled,
          ...(form.clientId || form.clientSecret ? { clientId: form.clientId.trim(), clientSecret: form.clientSecret.trim() } : {}) };
    const res = await saveIntegration(district, settings);
    setMsg(res.ok ? 'Saved.' : res.message);
    setBusy(false);
    load();
  };
  const sync = async () => {
    setBusy(true); setMsg('');
    const res = await syncNow(district);
    setMsg(res.message);
    setBusy(false);
    setTimeout(load, 60000);
  };
  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) { setMsg('Choose the .zip file of OneRoster CSVs.'); return; }
    setBusy(true); setMsg('Uploading…');
    const res = await uploadRosterZip(district, file);
    setMsg(res.message || (res.ok ? 'Uploaded. Results appear here in a minute or two.' : ''));
    setBusy(false);
    setTimeout(load, 45000);
  };

  const border = `1px solid ${theme.palette.custom?.gray || '#ccc'}`;
  const muted = { fontSize: '14px', color: theme.palette.text?.secondary || '#666', lineHeight: 1.5 };
  const last = state?.runs?.[0];
  const s = last?.summary;
  const sourceName = { oneroster: 'the SIS', clever: 'Clever' }[last?.source] || 'the SIS';
  const savedSource = state?.source;

  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', marginLeft: '60px', marginRight: '60px', marginTop: '40px',
                     width: 'auto', paddingLeft: '0px !important', border, borderRadius: '4px' }}>
      <Box sx={{ borderBottom: border }}>
        <Typography sx={{ fontSize: '20px', fontWeight: 700, margin: '20px' }}>SIS connection</Typography>
      </Box>
      <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Typography sx={muted}>
          Brings in the district's roster from its student information system. Each sync saves student demographics and
          class schedules (kept on the server only) and compares the rosters. It does not create or remove accounts yet.
        </Typography>
        <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_, v) => v && setMode(v)}>
          <ToggleButton value="oneroster" sx={{ textTransform: 'none' }}>OneRoster (recommended)</ToggleButton>
          <ToggleButton value="file" sx={{ textTransform: 'none' }}>Upload roster files</ToggleButton>
          <ToggleButton value="clever" sx={{ textTransform: 'none' }}>Clever</ToggleButton>
        </ToggleButtonGroup>

        {mode === 'oneroster' && (
          <>
            <Typography sx={muted}>
              Free. Works with PowerSchool, Infinite Campus, Skyward, ClassLink and Ed-Fi. Ask the district's IT team for
              their OneRoster address and a read-only client ID and secret for DiscloSure.
            </Typography>
            <Box>
              <Label htmlFor="or-url">Roster server address</Label>
              <TextField id="or-url" size="small" fullWidth placeholder="https://…/ims/oneroster/v1p1"
                         value={form.oneRosterBaseUrl} onChange={set('oneRosterBaseUrl')} />
            </Box>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <Box>
                <Label htmlFor="or-auth">Sign-in type</Label>
                <TextField id="or-auth" select size="small" value={form.oneRosterAuth} onChange={set('oneRosterAuth')} sx={{ minWidth: 220 }}>
                  <MenuItem value="oauth2">OAuth 2 (most servers)</MenuItem>
                  <MenuItem value="oauth1">OAuth 1 (older OneRoster 1.1)</MenuItem>
                </TextField>
              </Box>
              {form.oneRosterAuth === 'oauth2' && (
                <Box sx={{ flex: 1, minWidth: 280 }}>
                  <Label htmlFor="or-token">Token URL</Label>
                  <TextField id="or-token" size="small" fullWidth placeholder="https://…/oauth/token"
                             value={form.oneRosterTokenUrl} onChange={set('oneRosterTokenUrl')} />
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Label htmlFor="or-id">Client ID</Label>
                <TextField id="or-id" size="small" fullWidth value={form.clientId} onChange={set('clientId')}
                           placeholder={state?.credentialsSaved ? 'Saved — leave blank to keep' : ''} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Label htmlFor="or-secret">Client secret</Label>
                <TextField id="or-secret" size="small" fullWidth type="password" autoComplete="new-password"
                           value={form.clientSecret} onChange={set('clientSecret')}
                           placeholder={state?.credentialsSaved ? 'Saved — leave blank to keep' : ''} />
              </Box>
            </Box>
          </>
        )}

        {mode === 'file' && (
          <>
            <Typography sx={muted}>
              For districts that export files instead. Upload the OneRoster CSV export as one .zip (users, orgs, classes,
              enrollments and, if available, demographics). The file is processed once and then deleted.
            </Typography>
            <Box>
              <Button variant="contained" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}
                      sx={{ textTransform: 'none', boxShadow: 'none' }}>
                Upload roster files (.zip)
              </Button>
              <input ref={fileRef} type="file" accept=".zip,application/zip" hidden onChange={onFile} />
            </Box>
          </>
        )}

        {mode === 'clever' && (
          <>
            <Typography sx={muted}>Clever charges DiscloSure per district, so use it only when a district requires Clever.</Typography>
            {state && !state.configured && (
              <Typography sx={{ fontSize: '13px', color: theme.palette.warning?.dark || '#b26a00' }}>
                DiscloSure's Clever app credentials haven't been added in AWS yet, so syncs will fail until they are.
              </Typography>
            )}
            <Box>
              <Label htmlFor="clever-district-id">Clever district ID</Label>
              <TextField size="small" id="clever-district-id" placeholder="e.g. 6ae364606eb20b1662666199"
                         value={form.cleverDistrictId} onChange={set('cleverDistrictId')} sx={{ minWidth: '320px' }} />
            </Box>
          </>
        )}

        {mode !== 'file' && (
          <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch checked={form.enabled} onChange={set('enabled')} />
              <Typography sx={{ fontSize: '14px' }}>{form.enabled ? 'Nightly sync on' : 'Nightly sync off'}</Typography>
            </Box>
            <Button variant="contained" onClick={save} disabled={busy} sx={{ textTransform: 'none', boxShadow: 'none' }}>Save</Button>
            <Button variant="outlined" onClick={sync} disabled={busy || !state?.enabled || savedSource !== mode}
                    sx={{ textTransform: 'none' }}>Sync now</Button>
          </Box>
        )}
        {msg && <Typography sx={muted}>{msg}</Typography>}
        {last && (
          <Box sx={{ fontSize: '14px' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
              Last sync: {new Date(last.startedAt).toLocaleString()} ({last.status}{last.source ? `, ${last.source === 'oneroster' ? (last.trigger === 'upload' ? 'file upload' : 'OneRoster') : 'Clever'}` : ''})
            </Typography>
            {last.error && <Typography sx={{ fontSize: '13px', color: theme.palette.error?.main || '#d32f2f' }}>{last.error}</Typography>}
            {s && (
              <Typography sx={muted}>
                {s.cleverStudents} students in {sourceName} · {s.matchedStudents} matched to DiscloSure ·
                {' '}{s.inCleverNotDisclosure} only in {sourceName} · {s.inDisclosureNotClever} only in DiscloSure ·
                {' '}demographics saved for {s.demographicsSaved} · {s.schoolsMatchedByName} of {s.cleverSchools} schools matched by name
                {s.schedulesSaved !== undefined ? ` · class schedules saved for ${s.schedulesSaved}` : ''}
              </Typography>
            )}
            {s?.schedulesError && <Typography sx={muted}>Class schedules: {s.schedulesError}</Typography>}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default SisPanel;
